<template>
  <div ref="rootEl" class="mt-6 sm:mt-5">
    <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
      <label
        :for="`${name}-input`"
        class="block text-sm font-medium leading-5 text-gray-700 sm:mt-px sm:pt-2"
      >
        {{ label }}
      </label>

      <div class="mt-1 sm:mt-0 sm:col-span-2">
        <div class="relative" data-tag-selector>
          <div
            class="min-h-[42px] w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm focus-within:border-indigo-300 focus-within:ring-1 focus-within:ring-indigo-300"
          >
            <div class="flex flex-wrap items-center gap-1.5">
              <span
                v-for="tag in selected"
                :key="tag"
                class="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800"
              >
                {{ tag }}
                <button
                  type="button"
                  @click="removeTag(tag)"
                  class="rounded-full hover:bg-indigo-200 focus:outline-none"
                  :aria-label="`Remove tag ${tag}`"
                  :dusk="`remove-tag-${tag}`"
                >
                  <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 6l8 8M6 14L14 6"/>
                  </svg>
                </button>
              </span>

              <input
                :id="`${name}-input`"
                ref="inputEl"
                v-model="query"
                type="text"
                class="flex-1 min-w-[8rem] border-0 p-1 text-sm focus:outline-none focus:ring-0"
                :placeholder="selected.length ? '' : placeholder"
                :dusk="`${dusk}-input`"
                @focus="open = true"
                @keydown.enter.prevent="onEnter"
                @keydown.down.prevent="moveHighlight(1)"
                @keydown.up.prevent="moveHighlight(-1)"
                @keydown.backspace="onBackspace"
                @keydown.escape="open = false"
              />
            </div>
          </div>

          <ul
            v-if="open && (filteredOptions.length || canCreate)"
            class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
            role="listbox"
          >
            <li
              v-for="(option, index) in filteredOptions"
              :key="option"
              @mousedown.prevent="addTag(option)"
              @mouseover="highlight = index"
              :class="[
                'cursor-pointer select-none px-3 py-2',
                highlight === index ? 'bg-indigo-600 text-white' : 'text-gray-900'
              ]"
              :dusk="`option-${option}`"
              role="option"
            >
              {{ option }}
            </li>

            <li
              v-if="canCreate"
              @mousedown.prevent="addTag(normalizedQuery)"
              @mouseover="highlight = filteredOptions.length"
              :class="[
                'cursor-pointer select-none px-3 py-2 italic',
                highlight === filteredOptions.length ? 'bg-indigo-600 text-white' : 'text-gray-700'
              ]"
              :dusk="`create-tag-${normalizedQuery}`"
              role="option"
            >
              Create &ldquo;{{ normalizedQuery }}&rdquo;
            </li>
          </ul>
        </div>
        <p class="mt-1 text-xs text-gray-500">
          {{ helpText }}
        </p>
      </div>
    </div>

    <input
      v-for="tag in selected"
      :key="`hidden-${tag}`"
      type="hidden"
      :name="`${name}[]`"
      :value="tag"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';

const props = defineProps({
  availableTags: {
    type: Array,
    default: () => [],
  },
  selectedTags: {
    type: Array,
    default: () => [],
  },
  name: {
    type: String,
    default: 'tags',
  },
  label: {
    type: String,
    default: 'Tags',
  },
  placeholder: {
    type: String,
    default: 'Search or create tags…',
  },
  helpText: {
    type: String,
    default: 'Search existing tags or type a new one.',
  },
  dusk: {
    type: String,
    default: 'tag-selector',
  },
});

const normalize = (value) => String(value ?? '').trim().toLowerCase();

const selected = ref(
  Array.from(new Set((props.selectedTags || []).map(normalize).filter(Boolean)))
);
const query = ref('');
const open = ref(false);
const highlight = ref(0);
const rootEl = ref(null);
const inputEl = ref(null);

const allOptionNames = computed(() =>
  (props.availableTags || []).map((t) => normalize(t.name ?? t)).filter(Boolean)
);

const normalizedQuery = computed(() => normalize(query.value));

const filteredOptions = computed(() => {
  const q = normalizedQuery.value;
  return allOptionNames.value
    .filter((name) => !selected.value.includes(name))
    .filter((name) => !q || name.includes(q));
});

const canCreate = computed(() => {
  const q = normalizedQuery.value;
  if (!q) return false;
  if (selected.value.includes(q)) return false;
  if (allOptionNames.value.includes(q)) return false;
  return true;
});

watch([filteredOptions, canCreate], () => {
  highlight.value = 0;
});

function addTag(name) {
  const tag = normalize(name);
  if (!tag) return;
  if (!selected.value.includes(tag)) {
    selected.value.push(tag);
  }
  query.value = '';
  highlight.value = 0;
  inputEl.value?.focus();
}

function removeTag(name) {
  selected.value = selected.value.filter((t) => t !== name);
}

function moveHighlight(delta) {
  open.value = true;
  const total = filteredOptions.value.length + (canCreate.value ? 1 : 0);
  if (total === 0) return;
  highlight.value = (highlight.value + delta + total) % total;
}

function onEnter() {
  const total = filteredOptions.value.length + (canCreate.value ? 1 : 0);
  if (total === 0) {
    if (canCreate.value) addTag(normalizedQuery.value);
    return;
  }
  if (highlight.value < filteredOptions.value.length) {
    addTag(filteredOptions.value[highlight.value]);
  } else if (canCreate.value) {
    addTag(normalizedQuery.value);
  }
}

function onBackspace() {
  if (query.value === '' && selected.value.length > 0) {
    selected.value.pop();
  }
}

function handleClickOutside(event) {
  if (!rootEl.value) return;
  const inside = rootEl.value
    .querySelector('[data-tag-selector]')
    ?.contains(event.target);
  if (!inside) {
    open.value = false;
  }
}

onMounted(() => document.addEventListener('mousedown', handleClickOutside));
onBeforeUnmount(() => document.removeEventListener('mousedown', handleClickOutside));
</script>
